// PROTOTYPE ONLY (087 Super Match, ticket 10). See bestMatchPrototype.data.ts.
// Approved position: directly below the care-stage section.
import { motion } from 'framer-motion';
import { Gem, MapPin, Star } from 'lucide-react';
import { avatarColorClass, getInitials } from '../avatar';
import type { BestMatchResult } from './bestMatchPrototype.data';

type VariantCSpotlightStripProps = {
  result: BestMatchResult;
};

export const VariantCSpotlightStrip = ({ result }: VariantCSpotlightStripProps) => {
  if (result.status === 'loading') {
    return <div className="h-20 w-full animate-pulse rounded-2xl bg-card-subtle" aria-hidden />;
  }

  if (result.status === 'error') {
    return (
      <p className="text-xs text-text-secondary">
        Best Matches unavailable right now — your specialty results below are unaffected.
      </p>
    );
  }

  if (result.entries.length === 0) return null;

  const [entry] = result.entries;
  const rating =
    typeof entry.provider.rating === 'number' && entry.provider.rating > 0
      ? entry.provider.rating.toFixed(1)
      : null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[hsl(38_55%_68%)]/50 bg-gradient-to-r from-[hsl(43_70%_96%)] to-[hsl(38_65%_92%)] px-5 py-4 shadow-[0_3px_14px_rgba(160,120,30,0.15)]"
    >
      <motion.div
        className="pointer-events-none absolute inset-y-0 -left-1/4 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent"
        animate={{ x: ['0%', '520%'] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 3.4, ease: 'easeInOut' }}
        aria-hidden
      />

      <div className="relative flex items-center gap-4">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-[hsl(38_55%_65%)]/60 bg-gradient-to-br from-[hsl(43_78%_92%)] to-[hsl(30_70%_80%)] text-[hsl(30_65%_26%)] shadow-[0_1px_4px_rgba(160,120,30,0.25)]">
          <Gem className="h-4.5 w-4.5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1 overflow-hidden">
          <p
            title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
            className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(32_55%_38%)]"
          >
            Best Match
          </p>

          <div className="mt-0.5 flex items-center gap-2.5">
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${avatarColorClass(entry.provider.name)}`}
              aria-hidden
            >
              {getInitials(entry.provider.name)}
            </span>
            <div className="min-w-0">
              <a
                href={`/providers/${entry.provider.id}?discoverySource=NAVIGATOR`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-termina text-sm font-medium text-text-primary hover:text-sage hover:underline"
              >
                {entry.provider.name}
              </a>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-secondary">
                <span>{entry.category.name}</span>
                {entry.provider.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {entry.provider.location}
                  </span>
                )}
                {rating && (
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-sage text-sage" aria-hidden />
                    {rating}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
